/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * DataSectionPanel — shared editor panel for all data section types.
 * Provides: section heading (title/description/alignment/colors) and
 * "Show More" button configuration that navigates to /p/:tableSlug.
 */

import React from 'react';
import EditorField from './_EditorField';
import { useTheme } from '../../../theme/ThemeContext';

// ── Shared UI helpers ────────────────────────────────────────────────────────

function SectionDivider({ label }) {
  return (
    <div style={{ margin: '18px 0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
    </div>
  );
}

function ColorPicker({ value, onChange, presets }) {
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
      {presets.map(p => (
        <button
          key={p.color + p.label}
          type="button"
          title={p.label}
          onClick={() => onChange(p.color)}
          style={{
            width: 22, height: 22, borderRadius: 4, padding: 0, flexShrink: 0,
            border: value === p.color ? '2px solid #334155' : '1px solid #cbd5e1',
            cursor: 'pointer',
            background: p.color === ''
              ? 'repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 0 0 / 10px 10px'
              : p.color,
          }}
        />
      ))}
      <input
        type="color"
        value={value && value !== '' ? value : '#ffffff'}
        onChange={e => onChange(e.target.value)}
        style={{ width: 28, height: 22, padding: 1, border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
        title="Custom color"
      />
      {value && value !== '' && <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>{value}</span>}
      {value !== '' && (
        <button type="button" onClick={() => onChange('')} style={{ fontSize: 10, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Reset</button>
      )}
    </div>
  );
}

function SegmentedControl({ value, onChange, options }) {
  return (
    <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
      {options.map((opt, i) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          style={{
            flex: 1, padding: '5px 0', border: 'none',
            borderRight: i < options.length - 1 ? '1px solid #e2e8f0' : 'none',
            background: value === opt.value ? '#1e293b' : '#fff',
            color: value === opt.value ? '#fff' : '#374151',
            fontSize: 11, fontWeight: value === opt.value ? 600 : 400,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}
        >
          {opt.icon && <i className={`bi ${opt.icon}`} style={{ fontSize: 12 }} />}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DataSectionPanel({ cfg, updateCfg, tableSlug }) {
  const { theme } = useTheme();
  const primaryHex = theme.primary || '#4e2e80';

  const TEXT_PRESETS = [
    { label: 'Default',     color: ''        },
    { label: 'Dark',        color: '#1e293b'  },
    { label: 'Gray',        color: '#475569'  },
    { label: 'Light Gray',  color: '#94a3b8'  },
    { label: 'White',       color: '#ffffff'  },
    { label: 'Primary',     color: primaryHex },
  ];

  const BTN_PRESETS = [
    { label: 'Primary',   color: primaryHex },
    { label: 'Dark',      color: '#1e293b'  },
    { label: 'Green',     color: '#16a34a'  },
    { label: 'Blue',      color: '#2563eb'  },
    { label: 'Red',       color: '#dc2626'  },
    { label: 'White',     color: '#ffffff'  },
  ];

  const showMore = cfg.show_more !== false;

  return (
    <div>

      {/* ── Section Heading ─────────────────────────────────────────────── */}
      <SectionDivider label="Section Heading" />

      <EditorField label="Title">
        <input
          className="form-control form-control-sm"
          value={cfg.section_title || ''}
          onChange={e => updateCfg({ section_title: e.target.value })}
          placeholder="e.g. Featured Products"
        />
      </EditorField>

      <EditorField label="Description">
        <textarea
          className="form-control form-control-sm"
          rows={2}
          value={cfg.section_description || ''}
          onChange={e => updateCfg({ section_description: e.target.value })}
          placeholder="Optional subtitle or description…"
        />
      </EditorField>

      <EditorField label="Heading Alignment">
        <SegmentedControl
          value={cfg.heading_align || 'left'}
          onChange={val => updateCfg({ heading_align: val })}
          options={[
            { value: 'left',   icon: 'bi-align-start',  label: 'Left'   },
            { value: 'center', icon: 'bi-align-center', label: 'Center' },
            { value: 'right',  icon: 'bi-align-end',    label: 'Right'  },
          ]}
        />
      </EditorField>

      <EditorField label="Title Color">
        <ColorPicker
          value={cfg.title_color || ''}
          onChange={val => updateCfg({ title_color: val })}
          presets={TEXT_PRESETS}
        />
      </EditorField>

      <EditorField label="Description Color">
        <ColorPicker
          value={cfg.description_color || ''}
          onChange={val => updateCfg({ description_color: val })}
          presets={TEXT_PRESETS}
        />
      </EditorField>

      {/* ── Show More Button ─────────────────────────────────────────────── */}
      <SectionDivider label="Show More Button" />

      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="showMoreToggle"
          checked={showMore}
          onChange={e => updateCfg({ show_more: e.target.checked })}
        />
        <label className="form-check-label" htmlFor="showMoreToggle" style={{ fontSize: 13 }}>
          Show "View All / Show More" button
        </label>
      </div>

      {showMore && !tableSlug && (
        <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 14, padding: '6px 10px', background: '#fef2f2', borderRadius: 6, border: '1px solid #fecaca' }}>
          <i className="bi bi-exclamation-triangle me-1" />
          Select a source table above to enable this button.
        </div>
      )}

      {showMore && tableSlug && (
        <>
          <EditorField label="Button Text">
            <input
              className="form-control form-control-sm"
              value={cfg.show_more_text || 'View All'}
              onChange={e => updateCfg({ show_more_text: e.target.value })}
              placeholder="View All, Show More, Browse…"
            />
          </EditorField>

          <EditorField label="Button Style">
            <SegmentedControl
              value={cfg.show_more_style || 'outline'}
              onChange={val => updateCfg({ show_more_style: val })}
              options={[
                { value: 'filled',  label: 'Filled'   },
                { value: 'outline', label: 'Outlined'  },
                { value: 'ghost',   label: 'Ghost'     },
              ]}
            />
          </EditorField>

          <EditorField label="Button Color">
            <ColorPicker
              value={cfg.show_more_color || primaryHex}
              onChange={val => updateCfg({ show_more_color: val })}
              presets={BTN_PRESETS}
            />
          </EditorField>

          <EditorField label="Button Alignment">
            <SegmentedControl
              value={cfg.show_more_align || 'center'}
              onChange={val => updateCfg({ show_more_align: val })}
              options={[
                { value: 'left',   icon: 'bi-align-start',  label: 'Left'   },
                { value: 'center', icon: 'bi-align-center', label: 'Center' },
                { value: 'right',  icon: 'bi-align-end',    label: 'Right'  },
              ]}
            />
          </EditorField>

          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: -8, padding: '5px 8px', background: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0' }}>
            <i className="bi bi-link-45deg me-1" />
            Links to: <strong>/p/{tableSlug}</strong>
          </div>
        </>
      )}

    </div>
  );
}
